import type { PortableTextBlock } from "@portabletext/types";
import type { Taxonomy as _Taxonomy } from "#/db/schema.ts";

export type JobListing = {
  title: string;
  city: string;
  hospital: string;
  salary: number;
  employmentContractType: string;
  positionTitle: string;
  postedAt: string;
  description: string;
  responsibilities: string[];
  teamDetails: string[];
  locationDetails: string[];
  benefits: string[];
};

export type ArticleProps = {
  title: string;
  slug: string;
  summary: string;
  body: PortableTextBlock[];
  mainImage: {
    url: string;
    alt: string;
    caption: string;
    width: number;
    height: number;
  };
  metaTitle: string;
  metaDescription: string;
  _updatedAt: string;
};

export type JobCityProps = {
  slug: string;
  name: string;
  state: string;
  stateCode: string;
  regionalCities: CityDescription[];
  rank: number;
  description: string;
  stateCities: [
    { name: string; description: string; slug: string; stateCode: string },
  ];
  stateLink?: { state: string; slug: string; description: string };
  anesthesiologistContent: AnesthesiologistContent;
  anesthesiologistSalary: {
    avg: number;
    min: number;
    max: number;
  };
  anesthesiologistJobPostings: JobListing[];
};

type CityDescription = {
  name: string;
  description: string;
};

type AnesthesiologistContent = {
  intro: string;
  medicalLicense: string;
};

export type SiteSettingsProps = {
  title: string;
  description: string;
  redirects: [
    {
      sourcePath: string;
      target: string;
    },
  ];
};

export type JobPostingSchema = {
  "@context": "https://schema.org";
  "@type": "JobPosting";
  title: string;
  description: string;
  identifier: {
    "@type": "PropertyValue";
    name: string;
    value: string;
  };
  employmentType: EmploymentType | EmploymentType[];
  datePosted: string;
  validThrough: string;
  hiringOrganization: {
    "@type": "Organization";
    name: string;
    sameAs: string;
    logo: string;
  };
  jobLocation: {
    "@type": "Place";
    address: {
      "@type": "PostalAddress";
      streetAddress: string;
      addressLocality: string;
      addressRegion: string;
      postalCode: string;
      addressCountry: string;
    };
  };
  baseSalary?: {
    "@type": "MonetaryAmount";
    currency: "USD";
    value: {
      "@type": "QuantitativeValue";
      minValue: number;
      maxValue: number;
      unitText: UnitText;
    };
  };
  directApply: boolean;
  applicationLocationRequirements: {
    "@type": "Country";
    name: "USA";
  };
  jobLocationType?: "TELECOMMUTE";
  industry: "Healthcare";
};

export enum EmploymentType {
  FULL_TIME = "FULL_TIME",
  PART_TIME = "PART_TIME",
  CONTRACTOR = "CONTRACTOR",
  TEMPORARY = "TEMPORARY",
  INTERN = "INTERN",
  VOLUNTEER = "VOLUNTEER",
  PER_DIEM = "PER_DIEM",
  OTHER = "OTHER",
}

export enum UnitText {
  HOUR = "HOUR",
  DAY = "DAY",
  WEEK = "WEEK",
  MONTH = "MONTH",
  YEAR = "YEAR",
}

export type OptionalJobPostingSchema = Partial<JobPostingSchema>;

export type HiringOrg = {
  name: string;
  slug: string;
  about: string;
  aboutJobPosting: string;
  metaTitle: string;
  metaDescription: string;
  stateName: string;
  youtubeVideoId: string | null;
  images: Array<Image>;
  contact: {
    url: string;
    streetAddress: string;
    cityName: string;
    stateId: string;
    postalCode: string;
    country: string;
    phone: string;
  };
};

export type Image = {
  assetId: string;
  url: string;
  alt: string;
  caption: string;
  width: number;
  height: number;
  imageType: "COVER" | "LOGO" | "OG_IMAGE" | "IMAGE_1" | "IMAGE_2";
};

export type Taxonomy = _Taxonomy;

export enum TaxonomyTag {
  PHYSICIAN = "physician",
  NURSE = "nurse",
  SPECIALTY = "specialty",
  ASSISTANT = "assistant",
}

export enum TaxonomyNameType {
  FULL_NAME = 1,
  ACRONYM = 2,
  ABBREVIATION = 3,
}
